/**
 * Resultaat-scherm: setstanden, winnaar, statistieken per scorebron,
 * export van eventlog en gelabelde clips.
 */

import { h, downloadJson } from "./dom";
import type { App } from "./app";
import { MatchEngine, type DomainEvent, type MatchState } from "../core";

export function renderResult(app: App, root: HTMLElement): void {
  const st = app.engine.state;
  const teams = st.config.teams;

  const setSummaries = summarizeSets(app.engine.events);

  const stats = deriveStats(st);

  const exportLog = h("button", {
    class: "btn",
    onclick: () => downloadJson(app.engine.events, `beachscore-eventlog-${Date.now()}.json`),
  }, ["⬇ Eventlog (JSON)"]);

  const exportClipsBtn = h("button", {
    class: "btn",
    onclick: async () => {
      try {
        const manifest = await app.clipStore.exportManifest();
        downloadJson(manifest, `beachscore-clips-manifest-${Date.now()}.json`);
      } catch {
        /* IndexedDB niet beschikbaar */
      }
    },
  }, ["⬇ Clips-manifest (JSON)"]);

  const newMatch = h("button", {
    class: "btn primary big",
    onclick: () => app.resetAll(),
  }, ["↺ Nieuwe wedstrijd"]);

  rows();
  function rows(): void {
    const setCard = h("fieldset", { class: "card" }, [
      h("legend", {}, ["Sets"]),
      ...setSummaries.map((s) =>
        h("div", { class: "set-summary" }, [
          h("span", {}, [`Set ${s.setNumber}: `]),
          h("strong", {}, [`${teams[s.winner].name} won `] ),
          h("span", {}, [`${s.points.A}–${s.points.B}`]),
        ]),
      ),
      st.matchWinner
        ? h("p", { class: "win-line" }, [`🏆 ${teams[st.matchWinner].name} wint de wedstrijd!`])
        : h("p", { class: "muted" }, ["Wedstrijd nog niet afgerond."]),
    ]);

    const statCard = h("fieldset", { class: "card" }, [
      h("legend", {}, ["Hoe zijn de punten tot stand gekomen?"]),
      h("div", { class: "stat-grid" }, [
        h("div", { class: "stat" }, [h("strong", {}, [String(stats.auto)]), h("span", {}, ["automatisch (vision, voorlopig → bevestigd)"])]),
        h("div", { class: "stat" }, [h("strong", {}, [String(stats.serveConfirmed)]), h("span", {}, ["via opslag bevestigd (zonder tweede punt)"])]),
        h("div", { class: "stat" }, [h("strong", {}, [String(stats.corrected)]), h("span", {}, ["gecorrigeerd (rollback via opslag/hand)"])]),
        h("div", { class: "stat" }, [h("strong", {}, [String(stats.manual)]), h("span", {}, ["handmatig ingevoerd"])]),
      ]),
      h("p", { class: "muted small" }, [`Totaal verwerkte rally's: ${st.history.length}`]),
    ]);

    const exportCard = h("fieldset", { class: "card" }, [
      h("legend", {}, ["Export"]),
      h("p", { class: "muted small" }, [
        "Alles blijft lokaal: de eventlog en eventueel gelabelde clips worden NIET automatisch geüpload. De bestanden kun je zelf delen.",
      ]),
      h("div", { class: "btn-row" }, [exportLog, exportClipsBtn]),
      newMatch,
    ]);

    root.append(
      h("div", { class: "topnav" }, [
        h("button", { class: "btn small ghost", onclick: () => app.go("live") }, ["← Scorebord"]),
        h("span", { class: "muted small" }, ["Wedstrijdlog"]),
      ]),
      setCard,
      statCard,
      exportCard,
    );
  }
}

interface SetSummary {
  setNumber: number;
  points: { A: number; B: number };
  winner: "A" | "B";
}

/** Bepaal per set de eindstand door per SET_STARTED de prefix te replayen. */
function summarizeSets(events: readonly DomainEvent[]): SetSummary[] {
  const out: SetSummary[] = [];
  const starts = events.map((e, i) => ({ e, i })).filter(({ e }) => e.type === "SET_STARTED");
  for (const { e, i } of starts) {
    const se = e as Extract<DomainEvent, { type: "SET_STARTED" }>;
    // eindpunt: tot aan de volgende SET_STARTED (of einde)
    const next = starts.find(({ i: j }) => j > i);
    const end = next ? next.i : events.length;
    if (end <= i) continue;
    const st = MatchEngine.fromEvents(events.slice(0, end)).state;
    if (st.setNumber !== se.setNumber) continue;
    if (st.currentSet.setNumber !== se.setNumber) continue;
    const winner = st.currentSet.winner;
    if (!winner) continue;
    out.push({ setNumber: se.setNumber, points: { ...st.currentSet.points }, winner });
  }
  return out;
}

function deriveStats(st: MatchState): { auto: number; serveConfirmed: number; corrected: number; manual: number } {
  let auto = 0;
  let serveConfirmed = 0;
  let corrected = 0;
  let manual = 0;
  for (const r of st.history) {
    const res = r.result;
    if (!res) continue;
    if (res.manual) manual += 1;
    else if (res.source === "correction" || res.confirmedBy === "correction") corrected += 1;
    else if (res.source === "serve") serveConfirmed += 1;
    else if (res.source === "vision") {
      if (res.confirmedBy === "serve") serveConfirmed += 1;
      else auto += 1;
    }
  }
  return { auto, serveConfirmed, corrected, manual };
}
